"use client"

import { cn } from "@/utils/classnames"
import React, { forwardRef, useMemo } from "react"
import { TextElement, TextVariant } from "@/theme/typography"

export interface TextProps extends Omit<React.HTMLAttributes<HTMLElement>, "color"> {
  children?: React.ReactNode
  variant?: TextVariant // Corresponding to Tailwind's typography classes
  textTransform?: string // Tailwind text-transform classes
  className?: string
  hyphenate?: boolean
  maxLines?: number | null
  element?: TextElement
}

const getVariant = (variant: TextVariant): string => {
  switch (variant) {
    case "h1":
      return "text-h1-sm md:text-h1-md lg:text-h1-lg"
    case "h2":
      return "text-h2-sm md:text-h2-md lg:text-h2-lg"
    case "h3":
      return "text-h3-sm md:text-h3-md lg:text-h3-lg"
    case "h4":
      return "text-h4-sm md:text-h4-md lg:text-h4-lg"
    case "h5":
      return "text-h5-sm md:text-h5-md lg:text-h5-lg"
    case "h6":
      return "text-h6-sm md:text-h6-md lg:text-h6-lg"
    case "eyebrow":
      return "text-eyebrow-sm md:text-eyebrow-md lg:text-eyebrow-lg"
    case "body1":
      return "text-body1-sm md:text-body1-md lg:text-body1-lg"
    case "body2":
      return "text-body2-sm md:text-body2-md lg:text-body2-lg"
    case "body3":
      return "text-body3-sm md:text-body3-md lg:text-body3-lg"
    case "caption":
      return "text-caption-sm md:text-caption-md lg:text-caption-lg"
    case "CTALarge":
      return "text-CTALarge-sm md:text-CTALarge-md lg:text-CTALarge-lg"
    case "CTASmall":
      return "text-CTASmall-sm md:text-CTASmall-md lg:text-CTASmall-lg"
    case "label":
      return "text-label-sm md:text-label-md lg:text-label-lg"
    default:
      return "text-body1-sm md:text-body1-md lg:text-body1-lg"
  }
}

const Text = forwardRef<any, TextProps>(
  (
    {
      children,
      variant = "body1",
      textTransform = "normal-case",
      className = "",
      hyphenate = false,
      maxLines = null,
      element,
      ...rest
    },
    ref
  ) => {
    const variantClass = getVariant(variant)

    const styles = useMemo(() => {
      const overFlowStyles: React.CSSProperties = {}
      if (maxLines) {
        overFlowStyles.overflow = "hidden"
        overFlowStyles.display = "-webkit-box"
        overFlowStyles.WebkitBoxOrient = "vertical"
        overFlowStyles.WebkitLineClamp = maxLines
      }

      return {
        fontVariantLigatures: "none",
        ...overFlowStyles,
      }
    }, [maxLines])

    const classes = cn("text-inherit", hyphenate && "break-words hyphens-auto", variantClass, textTransform, className)

    const elementType = element ? element : variant

    switch (elementType) {
      case "h1":
        return (
          <h1 style={styles} className={classes} ref={ref} {...rest}>
            {children}
          </h1>
        )
      case "h2":
        return (
          <h2 style={styles} className={classes} ref={ref} {...rest}>
            {children}
          </h2>
        )
      case "h3":
        return (
          <h3 style={styles} className={classes} ref={ref} {...rest}>
            {children}
          </h3>
        )
      case "h4":
        return (
          <h4 style={styles} className={classes} ref={ref} {...rest}>
            {children}
          </h4>
        )
      case "h5":
        return (
          <h5 style={styles} className={classes} ref={ref} {...rest}>
            {children}
          </h5>
        )
      case "h6":
        return (
          <h6 style={styles} className={classes} ref={ref} {...rest}>
            {children}
          </h6>
        )
      case "span":
        return (
          <span style={styles} className={classes} ref={ref} {...rest}>
            {children}
          </span>
        )
      case "CTALarge":
      case "CTASmall":
      case "label":
        return (
          <span style={styles} className={classes} ref={ref} {...rest}>
            {children}
          </span>
        )
      case "eyebrow":
      case "caption":
      default:
        return (
          <p style={styles} className={classes} ref={ref} {...rest}>
            {children}
          </p>
        )
    }
  }
)

export default Text

Text.displayName = "Text"
